<!-- MCP App frame — renders an interactive Lymon design inline in the chat
     (ADR 33). Three modes:

      • design — a LIVE design via the native Vue renderer (DesignLiveView):
                   same-origin, reactive, values polled from design_render. No
                   iframe — works through the tunnel and on remote devices.
      • embed    — a dashboard's live embed `url` loaded as an iframe src (the
                   embed runs at its own origin; token expiry bridged via
                   show_dashboard). `allow-same-origin` only grants the frame its
                   own origin, never the portal's.
      • html     — fallback self-contained render (designer_render_view) via
                   `srcdoc` at a NULL origin, with an injected <base> so asset
                   URLs resolve. -->
<template>
  <div class="overflow-hidden rounded-xl border border-line bg-surface">
    <header class="flex h-10 items-center gap-2 border-b border-line bg-surface-2/50 px-3">
      <component :is="app.kind === 'design' ? Workflow : app.kind === 'app' ? Activity : LayoutDashboard" class="size-4 text-brand-600" />
      <span class="text-xs font-semibold text-ink">{{ app.kind === 'design' ? $t('chat-ui.appFrame.design') : app.kind === 'app' ? $t('chat-ui.appFrame.app') : $t('chat-ui.appFrame.dashboard') }}</span>
      <code class="truncate text-[11px] text-faint">{{ app.kind === 'design' ? currentDesignId : app.id }}</code>
      <span v-if="mode === 'design' || mode === 'app'" class="inline-flex items-center gap-1 text-[11px] text-success" :title="$t('chat-ui.appFrame.liveTitle')">
        <span class="size-1.5 rounded-full bg-success"></span>{{ $t('chat-ui.appFrame.live') }}
      </span>
      <span v-else-if="loading" class="text-[11px] text-faint">{{ mode === 'embed' ? $t('chat-ui.appFrame.refreshing') : $t('chat-ui.appFrame.rendering') }}</span>
      <div class="ml-auto flex items-center gap-0.5">
        <button v-if="mode !== 'design' && mode !== 'app'" type="button" class="flex size-7 items-center justify-center rounded-md text-muted transition-colors hover:bg-gray-100 hover:text-ink" :title="$t('common.refresh')" @click="refresh">
          <RefreshCw class="size-3.5" :class="loading ? 'animate-spin' : ''" />
        </button>
        <RouterLink v-if="app.kind !== 'app'" :to="editorHref" class="flex size-7 items-center justify-center rounded-md text-muted transition-colors hover:bg-gray-100 hover:text-ink" :title="$t('chat-ui.appFrame.openInEditor')">
          <PencilRuler class="size-3.5" />
        </RouterLink>
        <a v-if="mode === 'embed' && currentUrl" :href="currentUrl" target="_blank" rel="noopener" class="flex size-7 items-center justify-center rounded-md text-muted transition-colors hover:bg-gray-100 hover:text-ink" :title="$t('chat-ui.appFrame.openFull')">
          <ExternalLink class="size-3.5" />
        </a>
      </div>
    </header>

    <p v-if="error" class="flex items-center gap-2 bg-danger-soft px-3 py-2 text-xs text-danger">
      <TriangleAlert class="size-3.5 shrink-0" />{{ error }}
      <button type="button" class="ml-auto font-medium underline" @click="refresh">{{ $t('common.retry') }}</button>
    </p>

    <!-- LIVE design: native Vue renderer (no iframe). The flex box gives the
         canvas a definite height so its fit-to-frame zoom computes correctly. -->
    <div v-if="mode === 'design'" class="flex" :style="{ height: height + 'px' }">
      <DesignLiveView :key="currentDesignId" :design-id="currentDesignId" @navigate="onDesignNav" />
    </div>
    <!-- Dashboard embed: live iframe on the embed origin. -->
    <iframe
      v-else-if="mode === 'embed'"
      ref="frameEl"
      :src="currentUrl"
      class="block w-full border-0 bg-canvas"
      :style="{ height: height + 'px' }"
      sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
      referrerpolicy="no-referrer"
      :title="$t('chat-ui.appFrame.iframeTitle')"
      @load="onFrameLoad"
    ></iframe>
    <!-- Purpose-built MCP App (ADR 33 F3): same-origin file, NULL-origin sandbox
         (no allow-same-origin) — talks to the host only via the App SDK bridge. -->
    <iframe
      v-else-if="mode === 'app'"
      :src="props.app.url"
      class="block w-full border-0 bg-canvas"
      :style="{ height: height + 'px' }"
      sandbox="allow-scripts"
      referrerpolicy="no-referrer"
      :title="$t('chat-ui.appFrame.iframeTitle')"
      @load="onFrameLoad"
    ></iframe>
    <!-- Fallback: self-contained HTML render. -->
    <iframe
      v-else
      :srcdoc="currentHtml"
      class="block w-full border-0 bg-canvas"
      :style="{ height: height + 'px' }"
      sandbox="allow-scripts"
      referrerpolicy="no-referrer"
      :title="$t('chat-ui.appFrame.iframeTitle')"
      @load="onFrameLoad"
    ></iframe>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { LayoutDashboard, Workflow, Activity, RefreshCw, ExternalLink, PencilRuler, TriangleAlert } from 'lucide-vue-next';
import { callTool } from '../../api/client';
import { attachAppBridge, type DockAppBridge } from '../../composables/useAppBridge';
import { useI18n } from 'vue-i18n';
import DesignLiveView from '../../designer/DesignLiveView.vue';

const { t } = useI18n();
const props = defineProps<{
  app: { kind: 'dashboard' | 'design' | 'app'; id: string; uri?: string; url?: string; html?: string };
}>();

// Designs are 16:9 canvases — give them more room than a dashboard card. A ref so
// the MCP Apps bridge can resize the frame from `ui/notifications/size-changed`.
const height = ref(props.app.kind === 'design' ? 520 : props.app.kind === 'app' ? 300 : 420);
// Designs render live via the native Vue viewer; a purpose-built app loads its
// own URL sandboxed; dashboards use the embed URL (live) or an inline HTML render.
const mode = props.app.kind === 'design' ? 'design' : props.app.kind === 'app' ? 'app' : props.app.url ? 'embed' : 'html';
const currentUrl = ref(props.app.url ?? '');
const currentHtml = ref(props.app.html ? withBase(props.app.html) : '');
const loading = ref(false);
const error = ref<string | null>(null);
const frameEl = ref<HTMLIFrameElement | null>(null);

// The design shown inline. A 'navigate: design:<id>' button in the rendered
// design swaps THIS (re-renders in place) instead of opening the editor — that's
// the kiosk-style behaviour the chat dock wants.
const currentDesignId = ref(props.app.id);
function onDesignNav(target: { kind: 'design' | 'dashboard'; id: string }): void {
  if (target.kind === 'design') currentDesignId.value = target.id;
  // Dashboards have no inline live renderer here → open their editor in a new tab.
  else window.open(`/designer/${target.id}`, '_blank', 'noopener');
}

const editorHref = computed(() =>
  props.app.kind === 'design' ? `/design/${currentDesignId.value}` : `/designer/${props.app.id}`,
);

// srcdoc runs at a null origin, so the render's absolute-path asset URLs
// (`/api/assets/…`) have no origin to resolve against. Inject a <base> pointing
// at this portal's origin → the asset reads proxy through to the gateway.
function withBase(html: string): string {
  const base = `<base href="${window.location.origin}/">`;
  if (/<head[^>]*>/i.test(html)) return html.replace(/<head[^>]*>/i, (m) => m + base);
  if (/<html[^>]*>/i.test(html)) return html.replace(/<html[^>]*>/i, (m) => `${m}<head>${base}</head>`);
  return base + html;
}

// Re-mint (embed) or re-render (html). Designs auto-poll, so they have no
// manual refresh.
async function refresh(): Promise<void> {
  if (loading.value || mode === 'design') return;
  loading.value = true;
  error.value = null;
  try {
    if (mode === 'embed') {
      const res = await callTool<{ url?: string }>('show_dashboard', { dashboard_id: props.app.id });
      if (res?.url) currentUrl.value = res.url;
    } else {
      const res = await callTool<{ html?: string }>('designer_render_view', { doc_kind: props.app.kind, doc_id: props.app.id });
      if (res?.html) currentHtml.value = withBase(res.html);
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    loading.value = false;
  }
}

// The embed posts recovery hints to its parent when its token is expiring /
// expired (services/embed EmbedDashboard.vue). React only to messages from our
// frame and for this design, then re-mint.
function onMessage(ev: MessageEvent): void {
  if (mode !== 'embed') return;
  if (frameEl.value && ev.source !== frameEl.value.contentWindow) return;
  const data = ev.data as { type?: string; dashboard_id?: string } | null;
  if (!data || typeof data.type !== 'string') return;
  if (data.dashboard_id && data.dashboard_id !== props.app.id) return;
  if (data.type === 'lymon:request_refresh' || data.type === 'lymon:expiring_soon') void refresh();
}

// ADR 33 — attach the SEP-1865 host bridge to an iframe app once it loads. If the
// frame content doesn't speak the protocol, the bridge stays idle (no-op). The
// app's tool calls are proxied + consented; size changes resize the frame.
let bridge: DockAppBridge | null = null;
function onFrameLoad(ev: Event): void {
  if (mode === 'design') return;
  const frame = ev.target as HTMLIFrameElement;
  bridge?.close();
  bridge = attachAppBridge(frame, {
    onSizeChange: (h) => { height.value = Math.max(160, Math.min(1200, Math.round(h))); },
    confirm: (name) => Promise.resolve(window.confirm(t('chat-ui.appFrame.confirmTool', { name }))),
  });
}

onMounted(() => {
  window.addEventListener('message', onMessage);
  if (mode === 'html' && !currentHtml.value) void refresh();
});
onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage);
  bridge?.close();
});
</script>
